import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { motion } from 'framer-motion';
import { Check, HelpCircle, Calendar, Mail, BookOpen, UtensilsCrossed, CalendarDays, Sparkles } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5, ease: 'easeOut' }
};

const packages = [
  {
    name: 'Starter',
    price: '\u00a399',
    period: '/month',
    note: '24-month minimum term.',
    description: 'A professional website with hosting, security, and support. Add features as you need them.',
    differentiators: [
      'Up to 5 pages',
      'Semi-custom design',
      'You provide the content',
      'Standard support',
    ],
    cta: 'Get Started',
  },
  {
    name: 'Business',
    price: '\u00a3149',
    period: '/month',
    note: '24-month minimum term.',
    description: 'A fully custom site with content support. Includes 1 add-on feature of your choice.',
    differentiators: [
      'Up to 10 pages',
      'Fully custom design',
      'Content assistance included',
      'Priority support',
      '1 add-on feature included',
    ],
    cta: 'Start Your Project',
    popular: true,
  },
  {
    name: 'Professional',
    price: 'From \u00a3199',
    period: '/month',
    note: '24-month minimum term.',
    description: 'A bespoke site with dedicated content writing. Includes 2 add-on features of your choice.',
    differentiators: [
      'Unlimited pages',
      'Bespoke design',
      'We write your content',
      'Same-day support',
      '2 add-on features included',
    ],
    cta: 'Request a Quote',
  },
];

const addOns = [
  {
    icon: Calendar,
    title: 'Booking System',
    price: '20',
    period: '/mo',
    desc: 'Online booking forms with automated confirmations, approval workflow, and customer email notifications.',
    comparison: 'Calendly costs \u00a38\u201320/mo. Acuity costs \u00a314\u201345/mo.',
  },
  {
    icon: Mail,
    title: 'Email Marketing',
    price: '20',
    period: '/mo',
    desc: 'Built-in email subscriptions with double opt-in, subscriber management, and GDPR compliance.',
    comparison: 'Mailchimp costs \u00a310\u201315/mo. MailerLite costs \u00a38\u201313/mo.',
  },
  {
    icon: BookOpen,
    title: 'Blog',
    price: '20',
    period: '/mo',
    desc: 'Publish blog posts with a rich text editor, manage drafts, and boost your SEO with fresh content.',
    comparison: 'Ghost costs \u00a39\u201325/mo. WordPress from \u00a320/mo.',
  },
  {
    icon: CalendarDays,
    title: 'Events',
    price: '15',
    period: '/mo',
    desc: 'Create and manage events with dates, descriptions, and public listings on your website.',
    comparison: 'Eventbrite charges 3.5% + fees per ticket.',
  },
  {
    icon: UtensilsCrossed,
    title: 'Menu Management',
    price: '15',
    period: '/mo',
    desc: 'Manage your food or drink menu with categories, items, pricing, and display it beautifully on your site.',
    comparison: 'No standalone equivalent at this price.',
  },
];

const faqs = [
  {
    question: 'How does the monthly plan work?',
    answer: 'You pay a fixed monthly fee with nothing upfront. We design and build your website, and the subscription covers your site, hosting, security, updates, and support. Add the features your business needs and manage everything from your own admin portal.',
  },
  {
    question: 'What\u2019s included in every plan?',
    answer: 'Every plan includes a custom-designed website, mobile-responsive design, fast CDN hosting, SSL, firewall protection, SEO setup, Google Analytics, an admin portal branded to your business, contact form with email notifications, and ongoing support. Add-on features like bookings, email marketing, and blog are available on top.',
  },
  {
    question: 'Can I add or remove features later?',
    answer: 'Absolutely. You can add features anytime as your business grows, and remove them if you no longer need them. Your monthly payment adjusts accordingly. No long-term commitment on individual features.',
  },
  {
    question: 'Do I own the website?',
    answer: 'You subscribe to our managed platform, similar to Squarespace or Wix \u2014 but with a custom-designed site and far more capability. Your domain name is always yours. If you decide to leave after your minimum term, your domain goes with you.',
  },
  {
    question: 'What happens if I cancel?',
    answer: 'After your 24-month minimum term, you can cancel with 30 days\u2019 notice. The website will go offline, but your domain stays yours and any content you\u2019ve created can be exported. There are no exit fees or penalties.',
  },
  {
    question: 'How long does it take to build?',
    answer: 'Most websites are live within 1\u20132 weeks. We\u2019ll give you a clear timeline before we start. Professional plan sites with bespoke design and content writing may take a little longer.',
  },
  {
    question: 'What\u2019s the difference between the plans?',
    answer: 'The plans differ in the size and depth of your website \u2014 how many pages, how custom the design is, whether we help with content, and how fast we respond to support requests. Business includes 1 add-on feature and Professional includes 2. Beyond that, add individual features as you need them.',
  },
  {
    question: 'Can I upgrade my plan later?',
    answer: 'Yes, you can move to a higher plan anytime. We\u2019ll adjust your monthly payment and make the changes to your site \u2014 whether that\u2019s more pages, a design refresh, or content support.',
  },
];

const Pricing = () => {
  return (
    <div data-testid="pricing-page">
      {/* Hero */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeInUp} className="max-w-3xl mx-auto text-center">
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-primary mb-6">
              Your Website, Your Way
            </h1>
            <p className="text-lg text-muted-foreground">
              {'\u00a3'}0 upfront. Pick your plan, add the features your business needs,
              and only pay for what you use.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-8 md:py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-3 gap-8">
            {packages.map((pkg, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className={`h-full relative flex flex-col ${pkg.popular ? 'border-accent border-2 shadow-xl' : 'border-border/50'}`}>
                  {pkg.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-accent text-accent-foreground text-xs font-bold px-4 py-1 rounded-full whitespace-nowrap">
                      Most Popular
                    </div>
                  )}
                  <CardContent className="p-6 md:p-8 flex flex-col flex-1">
                    <div className="mb-6">
                      <h2 className="font-heading text-2xl font-bold text-foreground mb-2">
                        {pkg.name}
                      </h2>
                      <p className="text-sm text-muted-foreground mb-4">
                        {pkg.description}
                      </p>

                      <div className="flex items-baseline gap-1">
                        <span className="font-heading text-4xl font-bold text-primary">{pkg.price}</span>
                        <span className="text-lg text-muted-foreground">{pkg.period}</span>
                      </div>
                      <p className="text-muted-foreground text-xs mt-1">{pkg.note}</p>
                    </div>

                    <ul className="space-y-3 mb-8 flex-1">
                      {pkg.differentiators.map((item, i) => (
                        <li key={i} className="flex items-start gap-3 text-sm">
                          <Check className="w-5 h-5 text-accent shrink-0 mt-0.5" />
                          <span className="text-foreground">{item}</span>
                        </li>
                      ))}
                    </ul>

                    <Button
                      asChild
                      data-testid={`pricing-select-${pkg.name.toLowerCase().replace(/\s+/g, '-')}`}
                      className={`w-full rounded-full py-6 ${
                        pkg.popular
                          ? 'bg-accent text-accent-foreground hover:bg-accent/90'
                          : 'bg-primary text-primary-foreground hover:bg-primary/90'
                      }`}
                    >
                      <Link to="/contact">{pkg.cta}</Link>
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          <motion.div {...fadeInUp} className="text-center mt-10 max-w-2xl mx-auto">
            <p className="text-muted-foreground text-sm">
              All plans include hosting, security, SEO, analytics, admin portal, and ongoing support.
              Add features below to tailor your site to your business.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Add-on Features */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeInUp} className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-4">
              Add the Features You Need
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
              Every business is different. A salon needs bookings. A restaurant needs a menu.
              A consultant needs a blog. Pick what fits yours — add or remove anytime.
            </p>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
            {addOns.map((addon, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
              >
                <Card className="border-border/50 h-full">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                        <addon.icon className="w-5 h-5 text-accent" />
                      </div>
                      <div className="text-right">
                        <span className="font-heading text-xl font-bold text-primary">+{'\u00a3'}{addon.price}</span>
                        <span className="text-sm text-muted-foreground">{addon.period}</span>
                      </div>
                    </div>
                    <h3 className="font-heading text-lg font-semibold text-foreground mb-2">
                      {addon.title}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-3">{addon.desc}</p>
                    <p className="text-xs text-muted-foreground/70 italic">{addon.comparison}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Bundle Banner */}
          <motion.div {...fadeInUp}>
            <div className="bg-primary rounded-xl p-6 md:p-8 text-center">
              <div className="flex items-center justify-center gap-2 mb-3">
                <Sparkles className="w-5 h-5 text-accent" />
                <h3 className="font-heading text-xl font-bold text-primary-foreground">
                  Want Everything?
                </h3>
                <Sparkles className="w-5 h-5 text-accent" />
              </div>
              <p className="text-primary-foreground/80 mb-4 max-w-xl mx-auto">
                Add all five features for{' '}
                <span className="font-bold text-accent">{'\u00a3'}60/month</span>{' '}
                instead of {'\u00a3'}90{' \u2014 '}save {'\u00a3'}30 every month.
              </p>
              <Button
                asChild
                className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-full px-8 py-5"
              >
                <Link to="/contact">Get the Full Package</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What's Always Included */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeInUp} className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-4">
              Included With Every Plan
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Before you add a single feature, every plan comes with everything
              you need for a professional online presence.
            </p>
          </motion.div>
          <motion.div {...fadeInUp} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {[
              'Custom-designed website',
              'Mobile responsive',
              'Fast CDN hosting',
              'SSL certificate',
              'Firewall protection',
              'Admin portal access',
              'Contact form & enquiries',
              'SEO setup & optimisation',
              'Google Analytics',
              'Ongoing updates',
              'UK-based support',
              '\u00a30 upfront',
            ].map((item, index) => (
              <div key={index} className="flex items-center gap-3">
                <Check className="w-5 h-5 text-accent shrink-0" />
                <span className="text-sm text-foreground">{item}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <motion.div {...fadeInUp} className="text-center mb-12">
            <div className="w-12 h-12 bg-accent/10 rounded-xl flex items-center justify-center mx-auto mb-4">
              <HelpCircle className="w-6 h-6 text-accent" />
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-4">
              Common Questions
            </h2>
          </motion.div>
          <motion.div {...fadeInUp}>
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="border-border/50">
                  <AccordionTrigger className="text-left font-heading font-semibold hover:text-accent">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div {...fadeInUp}>
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
              Not Sure What You Need?
            </h2>
            <p className="text-primary-foreground/80 text-lg mb-8 max-w-2xl mx-auto">
              No worries — get in touch and we'll help you figure out the right plan
              and features for your business. No pressure, just honest advice.
            </p>
            <Button
              asChild
              data-testid="pricing-cta-btn"
              className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-full px-8 py-6 text-lg"
            >
              <Link to="/contact">Let's Chat</Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
